import {
  Button,
  Card,
  Checkbox,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from 'components/PageHeader/PageHeader';
import Popup from 'components/Popup/Popup';
import MoreMenu from 'components/table/MoreMenu';
import SearchNotFound from 'components/table/SearchNoFound';
import TableHeader from 'components/table/TableHeader';
import TableToolbar from 'components/table/TableToolbar';
import { getCompobotGuiVersionsTableHead } from 'constans/table-headers';
import SnackbarContext from 'contexts/snackbarContext';
import {
  handleClick,
  handleSelectAllClick,
  sortByColumn,
} from 'services/list.service';
import {
  getAllGuiVersions,
  getCompobotGuiVersionByCompobotId,
  editCompobotGuiVersion,
  deleteGuiVersion,
} from 'services/compobotGuiVersions.service';
import { CompobotGuiVersionType } from 'types/compobotGuiVersion.type';
import { Link as RouterLink } from 'react-router-dom';

export default function CompoBotGuiVersionsPage() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [selected, setSelected] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState('name');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { handleOpen } = useContext(SnackbarContext);
  const [guiVersionsList, setGuiVersionsList] = useState<CompobotGuiVersionType[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [pinCode, setPinCode] = useState<number | null>(null);
  const [openPinModal, setOpenPinModal] = useState(false);

  useEffect(() => {
    getGuiVersionsHandler();
  }, []);

  const getGuiVersionsHandler = () => {
    getAllGuiVersions().then((response) => {
      setGuiVersionsList(response.data);
    });
  };

  const searchFilter = useCallback(
    debounce((filterName) => {
      if (filterName) {
        getCompobotGuiVersionByCompobotId(filterName).then((response) => {
          setGuiVersionsList(response.data);
        });
      } else {
        getGuiVersionsHandler();
      }
    }, 300),
    [],
  );

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteGuiVersion = (versionName: string) => {
    deleteGuiVersion(versionName)
      .then(() => {
        const newList = guiVersionsList.filter(({ id }) => id !== versionName);
        setGuiVersionsList(newList);
        handleSelectAllClick(false, guiVersionsList);
        setOpenModal(false);
        handleOpen(t('organisationsPage.orgDeletedSuccessMessage'), 'success');
      })
      .catch(({ data }) => {
        handleOpen(data.message);
      });
  };

  /*  Kéne egy bulk delete backendre is
  const handleDeleteOrganizationBulk = (versionName: string[]) => {
    deleteGuiVersionsBulk(versionName)
      .then(() => {
        const newList = guiVersionsList.filter(
          ({ id }) => !versionName.includes(id),
        );
        setGuiVersionsList(newList);
        handleSelectAllClick(false, guiVersionsList);
        setOpenModal(false);
        //handleOpen(t('organisationsPage.orgsDeletedSuccessMessage'), 'success');
      })
      .catch(({ data }) => {
        handleOpen(data.message);
      });
  };
  */

  const deleteGuiVersionAction = () => {
    if (selected.length > 1) {
      //handleDeleteGuiVersionBulk(selected);
    } else {
      handleDeleteGuiVersion(selected[0]);
    }
  };

  const openModalHandler = (id?: string) => {
    setOpenModal(true);
    if (id) {
      setSelected([id]);
    }
  };

  const changePinHandler = () => {
    setOpenPinModal(false);
    getGuiVersionsHandler();
  };

  const sortedGuiVersions = sortByColumn(
    guiVersionsList,
    orderBy as keyof CompobotGuiVersionType,
    order,
  );

  const guiVersionTableHead = useMemo(getCompobotGuiVersionsTableHead, [language]);

  return (
    <Container>
      <PageHeader 
        title={t('compobotGuiVersionsPage.title')} 
        buttonTitle={t('compobotGuiVersionsPage.buttonTitle').toString()} 
        hasButton 
      />

      <Button 
        variant="outlined" sx={{ margin: '0px 5px 20px 5px' }} component={RouterLink} to={'/compobot-config'}>
        {t('compobotGuiVersionsPage.compobotConfigButton')}
      </Button>

      <Card>
        <TableToolbar
          numSelected={selected.length}
          onFilterName={searchFilter}
          deleteHandler={() => openModalHandler()}
        />

        <TableContainer>
          <Table>
            {
              <TableHeader
                order={order}
                orderBy={orderBy}
                headLabel={guiVersionTableHead}
                rowCount={guiVersionsList.length}
                numSelected={selected.length}
                onRequestSort={handleRequestSort}
                onSelectAllClick={(checked) =>
                  setSelected(handleSelectAllClick(checked, guiVersionsList))
                }
              />
            }
            <TableBody>
              {sortedGuiVersions
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const {
                    id,
                    name,
                    updateTime,
                    modifiedAt,
                    createdAt,
                    expirationTime,
                    dataPath,
                    description
                  } = row;
                  const isItemSelected = selected.indexOf(id) !== -1;

                  return (
                    <TableRow
                      hover
                      key={id}
                      tabIndex={-1}
                      role="checkbox"
                      selected={isItemSelected}
                      aria-checked={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onClick={() => setSelected(handleClick(id, selected))}
                        />
                      </TableCell>
                      <TableCell component="th" scope="row" padding="none">
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{ fontWeight: 600 }}
                          >
                            {name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="left">{new Date(updateTime).toLocaleString()}</TableCell>
                      <TableCell align="left">{new Date(expirationTime).toLocaleString()}</TableCell>
                      <TableCell align="left">{new Date(createdAt).toLocaleString()}</TableCell>
                      <TableCell align="left">{new Date(modifiedAt).toLocaleString()}</TableCell>
                      <TableCell align="left">{description}</TableCell>

                      <TableCell align="right">
                        <MoreMenu
                          onDelete={() => openModalHandler(id)}
                          id={id}
                          duplicate={() => editCompobotGuiVersion({ name: name + ' (copy)', updateTime, expirationTime, dataPath, description }) }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
            {!guiVersionsList.length && (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                    <SearchNotFound />
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={guiVersionsList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, page) => setPage(page)}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('pagination.rowsPerPage')}
        />
      </Card>
      <Popup
        type="delete"
        open={openModal}
        onConfirm={deleteGuiVersionAction}
        handleClose={() => setOpenModal(false)}
      />
    </Container>
  );
}
